import type { ApolloError } from "@apollo/client";
import { NetworkStatus, useQuery } from "@apollo/client";
import DailyEventsCalendar from "components/investorrelations/DailyEventsCalendar";
import lightFormat from "date-fns/lightFormat";
import AgendaQuery from "lib/queries/AgendaQuery";
import type { IAgendaQuery, IAgendaQueryVariables } from "lib/queries/__generated__/AgendaQuery.generated";
import parseDateYmd from "lib/utils/parseDateYmd";
import debounce from "lodash/fp/debounce";
import { Value } from "react-powerplug";
import type { IAgendaEventType, IIssuerType } from "types/graphqlTypes";
import { IOrderDirection } from "types/graphqlTypes";
import { ArrowDown10Icon, ArrowUp01Icon } from "lucide-react";
import React from "react";
import { useEventsCalendarSet, useEventsCalendarValue } from "components/investorrelations/hooks/hooks";
import { FormattedMessage, useIntl } from "react-intl";
import type { EventsCalenderFilterEventType } from "./enums";
import { EventsCalendarFilterRange } from "./enums";
import { getAgendaEventType, getAgendaIssuerType } from "./utils";

type EventsCalendarChildrenStateProps = {
  data?: IAgendaQuery;
  error?: ApolloError;
  loading: boolean;
  isLoadingMore: boolean;
  loadMoreEvents: () => void;
};

interface EventsCalendarProps {
  myPositions: boolean;
  isInSP500: boolean;
  isInBookmarks: boolean;
  searchQuery: string | null;
  eventType: EventsCalenderFilterEventType | null;
  issuerType: IIssuerType | null;
  period: EventsCalendarFilterRange | null;
  orderBy: IOrderDirection | null;
  calendarInterval: {
    startDate: Date;
    endDate: Date;
  };
  filterByEventTypes?: IAgendaEventType[];
  limit?: number;
  children: (state: EventsCalendarChildrenStateProps) => React.ReactNode;
}

export function EventsCalendar({
  myPositions,
  isInSP500,
  isInBookmarks,
  searchQuery,
  eventType,
  issuerType,
  period,
  orderBy,
  calendarInterval,
  filterByEventTypes,
  limit = 30,
  children,
}: EventsCalendarProps) {
  const setEventsCalendar = useEventsCalendarSet();

  const isYearlyFilteringWithoutSearch = period === EventsCalendarFilterRange.InTheYear && !searchQuery;

  const {
    data: currentData,
    previousData,
    loading,
    error,
    fetchMore,
    networkStatus,
  } = useQuery<IAgendaQuery, IAgendaQueryVariables>(AgendaQuery, {
    skip: isYearlyFilteringWithoutSearch,
    notifyOnNetworkStatusChange: true,
    variables: {
      filterByStartDate: lightFormat(calendarInterval.startDate, "yyyy-MM-dd"),
      filterByEndDate: lightFormat(calendarInterval.endDate, "yyyy-MM-dd"),
      filterByEventType: eventType ? getAgendaEventType(eventType) : (filterByEventTypes ?? null),
      filterByIssuerType: issuerType ? getAgendaIssuerType(issuerType) || null : null,
      filterByIssuersInPortfolio: myPositions ?? null,
      filterByIssuersInBookmarks: isInBookmarks ?? null,
      filterBySearchQuery: searchQuery ?? null,
      filterByIsInSnP500: isInSP500 ?? null,
      filterByIssuerIds: null,
      direction: orderBy || IOrderDirection.Descending,
      limit,
      cursor: null,
    },
    onCompleted: (data) => {
      setEventsCalendar(data);
    },
  });

  const data = loading && previousData ? previousData : currentData;

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;

  const loadMoreEvents = () => {
    if (!data) {
      return;
    }

    const { agenda } = data;
    fetchMore({
      variables: {
        cursor: agenda.pageInfo.cursor,
      },
      updateQuery: (previousQueryResult, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.agenda) {
          return previousQueryResult;
        }

        return {
          ...previousQueryResult,
          agenda: {
            ...fetchMoreResult.agenda,
            nodes: previousQueryResult.agenda.nodes?.concat(fetchMoreResult.agenda.nodes),
          },
        };
      },
    });
  };

  return (
    <div className="grid w-full grid-cols-12 gap-x-2 gap-y-3 py-4 xl:gap-x-16 xl:px-0">
      {children({ data, error, loading, isLoadingMore, loadMoreEvents })}
    </div>
  );
}

interface EventsCalendarSearchBarProps {
  searchQuery: string | null;
  orderBy: IOrderDirection;
  setSearchQuery: (value: string | null) => void;
  setOrderBy: (value: IOrderDirection) => void;
}

export function EventsCalendarSearchBar({
  searchQuery,
  orderBy,
  setSearchQuery,
  setOrderBy,
}: EventsCalendarSearchBarProps) {
  const intl = useIntl();

  return (
    <div className="flex items-center space-x-4">
      <Value
        initial={searchQuery}
        onChange={debounce(400, (searchQuery) => {
          const safeSearchQuery = searchQuery.trim();

          setSearchQuery(safeSearchQuery);
        })}
      >
        {({ value, set }) => (
          <input
            type="text"
            className="flex w-full rounded-md border-gray-300 shadow-sm placeholder:tracking-wide placeholder:text-gray-400 focus:border-indigo-500 focus:ring-indigo-500"
            value={value || undefined}
            onChange={(event) => set(event.target.value)}
            placeholder={intl.formatMessage({
              defaultMessage: "Pesquise ativos pelo código de negociação...",
              id: "294TQS",
              description: "Agenda",
            })}
          />
        )}
      </Value>

      <button
        className="h-6 w-6 text-gray-400 hover:cursor-pointer hover:text-indigo-500"
        type="button"
        onClick={() => {
          setOrderBy(orderBy === IOrderDirection.Ascending ? IOrderDirection.Descending : IOrderDirection.Ascending);
        }}
      >
        {orderBy === IOrderDirection.Ascending ? (
          <ArrowUp01Icon strokeWidth={1.5} className="h-6 w-6" />
        ) : (
          <ArrowDown10Icon strokeWidth={1.5} className="h-6 w-6" />
        )}
      </button>
    </div>
  );
}

interface EventsCalendarListProps {
  period: EventsCalendarFilterRange | null;
  searchQuery: string | null;
  loading: boolean;
  isLoadingMore: boolean;
  error?: ApolloError | undefined;
  loadMoreEvents: () => void;
  setCalendarInterval: (value: { startDate: Date; endDate: Date }) => void;
}

export function EventsCalendarList({
  period,
  searchQuery,
  loading,
  isLoadingMore,
  error,
  loadMoreEvents,
  setCalendarInterval,
}: EventsCalendarListProps) {
  const isYearlyFilteringWithoutSearch = period === EventsCalendarFilterRange.InTheYear && !searchQuery;
  const eventsCalendar = useEventsCalendarValue();

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4 font-aeonik font-normal tracking-wide">
        <div className="flex">
          <div className="flex-1 md:flex md:justify-between">
            <p className="text-sm text-rose-700">
              <FormattedMessage
                defaultMessage="Não foi possível carregar os eventos. Por favor, tente novamente mais tarde."
                id="00HJni"
                description="Agenda"
              />
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isYearlyFilteringWithoutSearch) {
    return (
      <div className="rounded-md bg-yellow-50 p-4 font-aeonik font-normal tracking-wide">
        <div className="flex">
          <div className="flex-1 md:flex md:justify-between">
            <p className="text-sm text-amber-700">
              <FormattedMessage
                defaultMessage={`A utilização do filtro "Todo Período" requer o uso da busca por ativo!`}
                id="Bijx5S"
                description="Agenda"
              />
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <DailyEventsCalendar
      events={eventsCalendar}
      isLoading={loading}
      isLoadingMore={isLoadingMore}
      onLoadMoreEvents={() => loadMoreEvents()}
      onGoToNextAgendaEvent={() => {
        if (eventsCalendar && eventsCalendar.agenda.nextUpcomingEventDateRange) {
          setCalendarInterval({
            startDate: parseDateYmd(eventsCalendar.agenda.nextUpcomingEventDateRange.filterByStartDate),
            endDate: parseDateYmd(eventsCalendar.agenda.nextUpcomingEventDateRange.filterByEndDate),
          });
        }
      }}
    />
  );
}
